import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import '../../assets/styles/header.css';
import { BY_KEY, HEADER_NAV, HEADER_LABELS } from '../../config/siteRegistry';
import { sampleContent } from '../../assets/sampleContent/sampleContent';
import { track } from '../../utils/track';

// Verdict counts for the masthead ear, computed so they never drift from the cards.
const VERDICTS = sampleContent.reduce((acc, s) => {
  const v = String(s.titleBadge || '').toLowerCase();
  acc[v] = (acc[v] || 0) + 1;
  return acc;
}, {});

const dateline = () => {
  try {
    return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) {
    return '';
  }
};

export const Flag = ({ small = false }) => (
  <RouterLink className={`fi-flag${small ? ' fi-flag--sm' : ''}`} to="/" aria-label="EVs for Idiots home">
    EVs<span className="fi-for" aria-hidden="true">for</span>Idiots
  </RouterLink>
);

export const PageHeader = ({ handlePartSelect = () => {}, page = 'home' }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = page === 'home';
  const navSet = HEADER_NAV[page] || HEADER_NAV.home;

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, closeMenu]);

  const label = (item, mobile = false) => {
    if (item === '@content' && mobile) return HEADER_LABELS['@contentMobile'];
    if (HEADER_LABELS[item]) return HEADER_LABELS[item];
    return BY_KEY[item].navLabel;
  };

  // In-page actions stay buttons; everything else renders as a real link so
  // crawlers can follow it.
  const renderItem = (item, mobile = false) => {
    const text = label(item, mobile);
    if (item === '@search' || item === '@content') {
      const part = item === '@search' ? 'search' : 'content';
      return (
        <button
          type="button"
          className={mobile ? 'fi-drawer-link' : undefined}
          onClick={() => { closeMenu(); track('nav_click', { nav_item: part, placement: mobile ? 'drawer' : 'header' }); handlePartSelect(part); }}
        >
          {text}
        </button>
      );
    }
    const to = item === '@home' ? '/' : BY_KEY[item].href;
    return <RouterLink to={to} onClick={closeMenu}>{text}</RouterLink>;
  };

  const quizClick = () => { closeMenu(); if (window.gtag) window.gtag('event', 'nav_cta_quiz', {}); };
  const conciergeClick = () => { closeMenu(); if (window.gtag) window.gtag('event', 'nav_cta_concierge', {}); };

  return (
    <>
      {isHome && (
        <>
          <div className="fi-strip">
            <div className="fi-wrap">
              <span>{dateline()} · Free, like everything here</span>
              <span>{sampleContent.length} idiotic statements checked · Every claim sourced</span>
            </div>
          </div>
          <div className="fi-mast">
            <div className="fi-wrap fi-mast-top">
              <Flag />
              <div className="fi-ears">
                <div className="fi-ear">
                  <b>Today</b>
                  {VERDICTS.false || 0} idiotic statements busted. {VERDICTS.true || 0} annoyingly true. {VERDICTS.misleading || 0} misleading.
                </div>
                <div className="fi-ear fi-ear--pop">
                  <b>Price: $0</b>
                  Fact-checks, tools, the kit and the deal review.
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <nav className="fi-nav" data-sticky-nav aria-label="Main">
        <div className="fi-wrap">
          <div className="fi-nav-left">
            {!isHome && <Flag small />}
            <ul>
              {navSet.desktop.map((item) => <li key={item}>{renderItem(item)}</li>)}
            </ul>
          </div>
          <div className="fi-nav-ctas">
            <RouterLink className="fi-btn fi-btn--sm fi-hide-sm" to="/what-kind-of-idiot" onClick={quizClick}>
              Idiot quiz
            </RouterLink>
            <RouterLink className="fi-btn fi-btn--sm fi-btn--pop" to="/concierge" onClick={conciergeClick}>
              <span>Get help<span className="fi-hide-xs"> buying</span></span>
            </RouterLink>
            <button
              type="button"
              className="fi-btn fi-btn--sm fi-menu-btn"
              aria-expanded={menuOpen}
              aria-controls="fi-site-menu"
              onClick={() => { setMenuOpen(true); track('nav_menu_open', { page }); }}
            >
              Menu
            </button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div className="fi-drawer" onClick={closeMenu}>
          <div
            className="fi-drawer-panel"
            id="fi-site-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fi-drawer-head">
              <Flag small />
              <button type="button" className="fi-btn fi-btn--sm" onClick={closeMenu} autoFocus>Close</button>
            </div>
            {navSet.mobile.map((item) => <React.Fragment key={item}>{renderItem(item, true)}</React.Fragment>)}
            <RouterLink to="/what-kind-of-idiot" onClick={quizClick}>What kind of idiot are you?</RouterLink>
            {!navSet.mobile.includes('concierge') && (
              <RouterLink to="/concierge" onClick={conciergeClick}>Get help buying an EV</RouterLink>
            )}
          </div>
        </div>
      )}
    </>
  );
};
